import { useMemo } from 'react';
import { useFragment } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';
import { getFragment } from 'relay-runtime';
import type { Result } from 'relay-runtime';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { useEditorContentFragments } from './EditorContentFragmentsContext.ts';
import type {
	EditorContentFragmentKey,
	EditorContentNode,
} from './EditorContentFragmentsContext.ts';

type EditorContentFragmentRefKey = {
	readonly ' $data'?: unknown;
	readonly ' $fragmentSpreads': unknown;
};

/** The value of a @catch fragment read with its generated $key. */
export type EditorContentFragmentValue<TKey extends EditorContentFragmentRefKey> =
	NonNullable<TKey[' $data']> extends Result<infer TValue, unknown> ? TValue : never;

// The key of the fragment the $key reads, so a key of another fragment fails to compile.
type EditorContentFragmentKeyFor<TKey extends EditorContentFragmentRefKey> = {
	[K in EditorContentFragmentKey]: EditorContentNode[K] extends {
		' $fragmentSpreads': TKey[' $fragmentSpreads'];
	}
		? K
		: never;
}[EditorContentFragmentKey];

export type EditorContentFragmentResult<TData> = {
	isProvided: boolean;
	isLoading: boolean;
	data: TData | undefined;
	error: Error | undefined;
};

function identity<T>(value: T): T {
	return value;
}

/**
 * Reads a fragment of the editor document from EditorContentFragmentsContext. When it is provided,
 * its reader skips its own query. With skip, the fragment is not read. mapData must be a pure
 * function of the value.
 */
export function useEditorContentFragment<TKey extends EditorContentFragmentRefKey>(
	key: EditorContentFragmentKeyFor<TKey>,
	fragmentNode: GraphQLTaggedNode,
	options?: { skip?: boolean },
): EditorContentFragmentResult<EditorContentFragmentValue<TKey>>;
export function useEditorContentFragment<TKey extends EditorContentFragmentRefKey, TData>(
	key: EditorContentFragmentKeyFor<TKey>,
	fragmentNode: GraphQLTaggedNode,
	options: { skip?: boolean; mapData: (value: EditorContentFragmentValue<TKey>) => TData },
): EditorContentFragmentResult<TData>;
export function useEditorContentFragment<TKey extends EditorContentFragmentRefKey, TData>(
	key: EditorContentFragmentKeyFor<TKey>,
	fragmentNode: GraphQLTaggedNode,
	{
		skip = false,
		mapData,
	}: { skip?: boolean; mapData?: (value: EditorContentFragmentValue<TKey>) => TData } = {},
): EditorContentFragmentResult<TData | EditorContentFragmentValue<TKey>> {
	const fragment = useEditorContentFragments()[key];
	const result = useFragment<TKey>(
		fragmentNode,
		(fragment.isProvided && !skip ? fragment.ref : null) as TKey | null,
	) as Result<EditorContentFragmentValue<TKey>, unknown> | null;
	const isLoading = fragment.isProvided && !skip && fragment.isLoading;
	const stableMapData = useStableCallback(mapData ?? identity);

	return useMemo(
		() => ({
			isProvided: fragment.isProvided,
			isLoading,
			data: result?.ok ? stableMapData(result.value) : undefined,
			error:
				result?.ok === false
					? new Error(`${getFragment(fragmentNode).name} field error`, { cause: result.errors })
					: undefined,
		}),
		[fragment.isProvided, isLoading, result, stableMapData, fragmentNode],
	);
}
